import { useEffect } from 'react';
import cn from 'classnames';
import * as R from 'ramda';
import Papa from 'papaparse';
import { AutoSizer } from 'react-virtualized-auto-sizer';

import { formatNumberAsInt } from '@blisspointmedia/bpm-types/dist/formatUtils';

import { CellRenderer, StickyTable } from '@Components/StickyTable';
import { Size } from '@Components/designConstants';
import { EntityKind, EntityLogo } from '@Components/EntityLogo';

import {
  ALL_LENGTHS,
  ALL_NETWORKS,
  ColHeader,
  ExpandByMap,
  TABLE_LEFT_WIDTH,
  TABLE_MIN_COLUMN_WIDTH,
  TABLE_ROW_HEIGHT,
  TABLE_SCROLLBAR_SIZE,
  TABLE_TOP_HEIGHT,
  TopHeader
} from './competitive-metrics-constants';
import {
  getCsvHeaderLabel,
  getFirstPartOfHeaderName,
  getGreenShade,
  getRgbLimitsFromData,
  uniqueByEquals
} from './competitive-metrics-utils';
import { CostPersRawData } from './use-competitive-metrics-data';

interface NetworkLogoProps {
  network?: string;
}

const NetworkLogo = ({ network }: NetworkLogoProps) =>
  network ? (
    <EntityLogo kind={EntityKind.NETWORK} id={network} size={Size.LG} showFallbackName />
  ) : null;

interface MetricsTableContainerProps {
  costPersRawData: CostPersRawData;
  weekNames: string[];
  expandBy: ExpandByMap;
  setCsv: (csv: string) => void;
}

export const MetricsTableContainer = ({
  costPersRawData,
  weekNames,
  expandBy,
  setCsv
}: MetricsTableContainerProps) => {
  const { weeks } = costPersRawData;
  const { colHeaders, rowHeaders } = weeks[weeks.length - 1];
  const filteredWeeks = weeks.filter(weekData => weekNames.includes(weekData.name));

  const spots: number[][] = [];
  const spend: number[][] = [];
  for (let i = 0; i < rowHeaders.length; i++) {
    spots.push(new Array(colHeaders.length).fill(0));
    spend.push(new Array(colHeaders.length).fill(0));
  }

  for (const weekData of filteredWeeks) {
    for (let rowIndex = 0; rowIndex < rowHeaders.length; rowIndex++) {
      for (let colIndex = 0; colIndex < colHeaders.length; colIndex++) {
        spend[rowIndex][colIndex] += weekData.spend[rowIndex][colIndex];
        spots[rowIndex][colIndex] += weekData.spots[rowIndex][colIndex];
      }
    }
  }

  let ourRowHeaders = [...rowHeaders];
  let getOurRowIndex = (index: number): number => index;
  let ourColHeaders: ColHeader[] = colHeaders.map(origColHeader => {
    const [length, network] = origColHeader.split('_');
    return {
      network,
      length: parseInt(length.replace('s', ''), 10)
    };
  });

  ourColHeaders = [...ourColHeaders].sort((a, b) => {
    if (a.network !== b.network) {
      return (a.network ?? '') < (b.network ?? '') ? -1 : 1;
    }
    return (a.length ?? 0) - (b.length ?? 0);
  });
  const colIndexMap: Record<number, number> = {};
  for (let c = 0; c < colHeaders.length; c++) {
    const [length, network] = colHeaders[c].split('_');
    const newColHeader = {
      network,
      length: parseInt(length.replace('s', ''), 10)
    };
    colIndexMap[c] = ourColHeaders.findIndex(h => R.equals(h, newColHeader));
  }

  let getOurColIndex = (origColIndex: number): number => colIndexMap[origColIndex];
  const numericData = expandBy.spots ? spots : spend;
  if (!expandBy.dayPart) {
    ourRowHeaders = [...new Set(rowHeaders.map(getFirstPartOfHeaderName))];
    const dayPartHeaderMap: Record<number, number> = {};
    rowHeaders.forEach((val, idx) => {
      dayPartHeaderMap[idx] = ourRowHeaders.indexOf(getFirstPartOfHeaderName(val));
    });

    getOurRowIndex = origRowIndex => dayPartHeaderMap[origRowIndex];
  }

  if (!expandBy.network) {
    ourColHeaders = uniqueByEquals(ourColHeaders.map(({ network: _network, ...rest }) => rest));
    ourColHeaders = [...ourColHeaders].sort((a, b) => (a.length ?? 0) - (b.length ?? 0));
    const colHeaderIndexMap: Record<number, number> = {};
    colHeaders.forEach((val, idx) => {
      const length = parseInt(val.split('_')[0].replace('s', ''), 10);
      colHeaderIndexMap[idx] = ourColHeaders.findIndex(h => R.equals(h, { length }));
    });

    getOurColIndex = origColIndex => colHeaderIndexMap[origColIndex];
  }

  if (!expandBy.length) {
    ourColHeaders = uniqueByEquals(ourColHeaders.map(({ length: _length, ...rest }) => rest));
    if (ourColHeaders.length <= 1) {
      ourColHeaders = [];
    } else {
      ourColHeaders = [...ourColHeaders].sort((a, b) =>
        (a.network ?? '') < (b.network ?? '') ? -1 : 1
      );
    }

    const colHeaderIndexMap: Record<number, number> = {};
    colHeaders.forEach((val, idx) => {
      const network = val.split('_')[1];
      colHeaderIndexMap[idx] = ourColHeaders.findIndex(h => R.equals(h, { network }));
    });
    getOurColIndex = origColIndex => colHeaderIndexMap[origColIndex];
  }

  const rowTotals = new Array(ourColHeaders.length).fill(0);
  const columnTotals = new Array(ourRowHeaders.length).fill(0);
  const data: number[][] = [];
  for (let i = 0; i < ourRowHeaders.length; i++) {
    data.push(new Array(ourColHeaders.length).fill(0));
  }
  for (let rowIndex = 0; rowIndex < rowHeaders.length; rowIndex++) {
    const ourRowIndex = getOurRowIndex(rowIndex);
    for (let colIndex = 0; colIndex < colHeaders.length; colIndex++) {
      const ourColIndex = getOurColIndex(colIndex);
      const value = numericData[rowIndex][colIndex];
      columnTotals[ourRowIndex] += value;
      if (ourColIndex >= 0) {
        data[ourRowIndex][ourColIndex] += value;
        rowTotals[ourColIndex] += value;
      }
    }
  }

  const nonZeroColumnIndices: number[] = [];
  for (let index = rowTotals.length - 1; index >= 0; index--) {
    if (rowTotals[index] !== 0) {
      nonZeroColumnIndices.push(index);
    } else {
      rowTotals.splice(index, 1);
      ourColHeaders.splice(index, 1);
    }
  }
  ourRowHeaders.push('Total');
  const topHeaders: TopHeader[] = [ALL_NETWORKS, ...ourColHeaders];
  data.push(rowTotals);
  let totalTotal = 0;

  for (let i = 0; i < data.length - 1; i++) {
    for (let j = data[i].length - 1; j >= 0; --j) {
      if (!nonZeroColumnIndices.includes(j)) {
        data[i].splice(j, 1);
      }
    }
    data[i].unshift(columnTotals[i]);
    totalTotal += columnTotals[i];
  }
  data[ourRowHeaders.length - 1].unshift(totalTotal);

  const rgbLimits = getRgbLimitsFromData(data);

  const getLimit = (limit: 'min' | 'max', rowIndex: number, columnIndex: number): number => {
    if (columnIndex === 0) {
      return rgbLimits.networks[limit];
    }
    if (rowIndex === data.length - 1) {
      return rgbLimits.dayparts[limit];
    }
    return rgbLimits.details[limit];
  };

  const cellRenderer: CellRenderer<number> = ({
    style,
    columnIndex,
    rowIndex,
    classes,
    data: cellValue
  }) => (
    <div
      style={{
        ...style,
        backgroundColor:
          columnIndex === 0 && rowIndex === data.length - 1
            ? 'white'
            : getGreenShade(
                cellValue,
                getLimit('min', rowIndex, columnIndex),
                getLimit('max', rowIndex, columnIndex)
              )
      }}
      className={cn(classes, 'metric-cell')}
    >
      {expandBy.spots || cellValue === 0 ? '' : '$'}
      {cellValue === 0 ? '-' : formatNumberAsInt(cellValue)}
    </div>
  );

  const topRenderer: CellRenderer<TopHeader> = ({
    style,
    columnIndex,
    classes,
    data: cellValue
  }) => {
    if (cellValue) {
      return (
        <div style={style} className={cn(classes, 'header-logos')}>
          {/* The "All Networks" cell has no logo; other cells show the network logo. */}
          {(expandBy.network || !expandBy.length) &&
            (cellValue === ALL_NETWORKS ? (
              <div className='all-networks-label'>
                <span>{ALL_NETWORKS}</span>
              </div>
            ) : (
              <NetworkLogo network={cellValue.network} />
            ))}
          {/* This gives us the length labels. We only show them when length is expanded. Additionally, we don't want to
              show it on the "All Networks" cell (at column 0) when networks are also expanded. */}

          {expandBy.length && (columnIndex > 0 || !expandBy.network) && (
            <div className='length-label'>
              {cellValue === ALL_NETWORKS ? ALL_LENGTHS : `${cellValue.length}s`}
            </div>
          )}
        </div>
      );
    }
    return <div style={style} className={cn(classes)} />;
  };

  const leftRenderer: CellRenderer<string> = ({ style, classes, data: cellValue }) => {
    if (cellValue) {
      const tokens = cellValue.split('_');
      return (
        <div style={style} className={cn(classes, 'row-labels')}>
          <div className='row-label'>{tokens[0]}</div>
          {expandBy.dayPart && <div className='row-label-daypart'>{tokens[1]}</div>}
        </div>
      );
    }
    return (
      <div style={style} className={cn(classes, 'row-labels')}>
        {cellValue}
      </div>
    );
  };

  const headerRow = ['', ...topHeaders.map(getCsvHeaderLabel)];
  const csv = Papa.unparse([headerRow, ...data.map((row, i) => [ourRowHeaders[i], ...row])]);
  useEffect(() => {
    setCsv(csv);
  }, [csv, setCsv]);

  const tableHeight =
    TABLE_TOP_HEIGHT + ourRowHeaders.length * TABLE_ROW_HEIGHT + TABLE_SCROLLBAR_SIZE;

  return (
    <div className='metrics-table-container' data-testid='metrics-table'>
      <div className='metrics-table-box' style={{ height: tableHeight }}>
        <AutoSizer
          renderProp={({ width = 0 }) => (
            <StickyTable
              alternateColors
              width={width}
              height={tableHeight}
              leftWidth={TABLE_LEFT_WIDTH}
              topHeight={TABLE_TOP_HEIGHT}
              topData={topHeaders}
              leftData={ourRowHeaders}
              data={data}
              leftRenderer={leftRenderer}
              topRenderer={topRenderer}
              cellRenderer={cellRenderer}
              columnWidth={Math.max(
                TABLE_MIN_COLUMN_WIDTH,
                Math.floor((width - TABLE_LEFT_WIDTH - TABLE_SCROLLBAR_SIZE) / topHeaders.length)
              )}
              rowHeight={TABLE_ROW_HEIGHT}
            />
          )}
        />
      </div>
    </div>
  );
};
